/**
 * Constant object defining available microclimate options with their respective radius values and display labels.
 *
 * @property {Object} RADIUS_0_25_MILES - Represents a microclimate with a radius of 0.25 miles.
 * @property {Object} RADIUS_0_5_MILES - Represents a microclimate with a radius of 0.5 miles.
 * @property {Object} RADIUS_1_0_MILE - Represents a microclimate with a radius of 1 mile.
 *
 * @example
 * const microclimate = MICROCLIMATES.RADIUS_0_25_MILES;
 * console.log(microclimate.label); // Outputs '0.25 miles'
 * console.log(microclimate.radius); // Outputs 0.25
 * console.log(microclimate.value); // Outputs 'radius_0.25'
 * console.log(microclimate.badge); // Outputs '0.25 mi radius'
 */
import {
    MicroclimateSegment,
    RateBandKey,
} from 'api/rates/comparative-snapshot/comparative-snapshot.types';

export const MICROCLIMATES = {
    RADIUS_0_25_MILES: {
        value: 'radius025',
        radius: 0.25,
        label: '0.25 miles',
        badge: '0.25 mi radius',
        zoom: 14,
    },
    RADIUS_0_5_MILES: {
        value: 'radius05',
        radius: 0.5,
        label: '0.5 miles',
        badge: '0.5 mi radius',
        zoom: 13,
    },
    RADIUS_1_0_MILE: {
        value: 'radius10',
        radius: 1,
        label: '1 mile',
        badge: '1 mi radius',
        zoom: 12,
    },
} as const;

/**
 * Represents the structure of a tooltip for a rate band.
 *
 */
export interface TooltipContent {
    daysOfWeek: string;
    reservationStartTimesBetween: string[];
    reservationDurationsBetween: string;
}

/**
 * Constant defining content for each rate band tooltip
 *
 */
export const RATE_BAND_TOOLTIPS: Record<RateBandKey, TooltipContent> = {
    weekday3h: {
        daysOfWeek: 'Monday - Friday',
        reservationStartTimesBetween: ['Midnight-4:59AM', '10:00AM-3:59PM'],
        reservationDurationsBetween: '0-3 hours',
    },
    weekday6h: {
        daysOfWeek: 'Monday - Friday',
        reservationStartTimesBetween: ['Midnight-4:59AM', '10:00AM-3:59PM'],
        reservationDurationsBetween: '4-6 hours',
    },
    weekday12h: {
        daysOfWeek: 'Monday - Friday',
        reservationStartTimesBetween: ['Midnight-4:59AM', '10:00AM-3:59PM'],
        reservationDurationsBetween: '7-12 hours',
    },
    weekday18h: {
        daysOfWeek: 'Monday - Friday',
        reservationStartTimesBetween: ['Midnight-3:59AM'],
        reservationDurationsBetween: '13-18 hours',
    },
    weekday24h: {
        daysOfWeek: 'Monday - Friday',
        reservationStartTimesBetween: ['Midnight-11:59PM'],
        reservationDurationsBetween: '19-24 hours',
    },
    commuter: {
        daysOfWeek: 'Monday - Friday',
        reservationStartTimesBetween: ['5:00AM-9:59AM'],
        reservationDurationsBetween: '0-12 hours',
    },
    night: {
        daysOfWeek: 'Monday - Friday',
        reservationStartTimesBetween: ['4PM-11:59PM'],
        reservationDurationsBetween: '0-11 hours; Exit before 3AM',
    },
    overnight: {
        daysOfWeek: 'Monday - Friday',
        reservationStartTimesBetween: ['4PM-11:59PM'],
        reservationDurationsBetween: '4-18 hours; Exit after 3AM',
    },
    weekend3h: {
        daysOfWeek: 'Saturday - Sunday',
        reservationStartTimesBetween: ['Midnight-11:59PM'],
        reservationDurationsBetween: '0-3 hours',
    },
    weekend12h: {
        daysOfWeek: 'Saturday - Sunday',
        reservationStartTimesBetween: ['Midnight-11:59PM'],
        reservationDurationsBetween: '4-12 hours',
    },
    weekend24h: {
        daysOfWeek: 'Saturday - Sunday',
        reservationStartTimesBetween: ['Midnight-11:59PM'],
        reservationDurationsBetween: '13-24 hours',
    },
};

/* Content label for when there is not enough data */
export const NOT_ENOUGH_DATA = 'Not enough data';

/* Segment object for 'microclimate viewed' event */
export const createMicroclimateViewedEvent = (
    microclimateLabel: string,
    currentData: MicroclimateSegment[],
    facilityId: number
) => ({
    event: 'Rate Guidance - Microclimate Viewed',
    properties: {
        Label: microclimateLabel,
        Data: currentData,
        FacilityId: facilityId,
    },
});

/* Content for Rate Band table columns */
export const RATE_BAND_COLUMNS = {
    RATE_SEGMENT: {
        value: 'rateSegment',
        label: 'Common SpotHero Rate Bands',
        header: 'Common Transient Rate Bands on SpotHero',
    },
    HIGH_DEMAND: {
        value: 'isHighDemand',
        label: 'High Demand',
        header: 'High Demand',
    },
    CURRENTLY_SET: {
        value: 'currentRateRange',
        label: 'Currently Set',
        header: 'Currently Set',
    },
    PURCHASED_NEARBY: {
        value: 'nearbyRateRange',
        label: 'Purchased Nearby',
        header: 'Purchased Nearby',
    },
} as const;
